import { useCallback, useMemo } from 'react';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';
import { LinkIcon } from '@air/design-icons';

import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

/**
 * The collection-home crumb — "take me back to the link's root page" — built on the collection
 * tree's OWN navigation contract. The collection record comes from the tree's required
 * PublicShowcaseProvider (the record the layout was mounted with), not a fetch: the crumb renders
 * identically on every surface, and the internal surfaces (editor canvas, Preview iframe) never hit
 * viewer-gated public endpoints for it.
 *
 * The crumb is a button calling `onSelectPage(null)`, not a `type: 'link'` anchor: the link crumb
 * type carries no onClick (BreadcrumbsItemTypeLink picks only href/prefix/suffix), and the button
 * behaves identically on every surface — the URL surfaces' `onSelectPage` is already a shallow
 * route push to their own base path (the live /a page's, or the Preview iframe's — so the preview
 * stays inside its iframe instead of escaping to the live route), and the editor canvas's clears
 * its active page in state, which exits the drilled board and closes the modal with it.
 */
export const usePublicShowcaseHomeBreadcrumb = () => {
  const { collection, onSelectPage } = usePublicShowcaseContext();

  const handleGoToShowcaseHome = useCallback(() => {
    onSelectPage(null);
  }, [onSelectPage]);

  const showcaseHomeBreadcrumb = useMemo<BreadcrumbsItemType>(
    () => ({
      className: 'text-inherit',
      id: collection.id,
      prefix: <LinkIcon className="ads:text-icon-default text-gray-10" size="sm" />,
      label: collection.name,
      type: 'button',
      onClick: handleGoToShowcaseHome,
    }),
    [collection.id, collection.name, handleGoToShowcaseHome],
  );

  return { showcaseHomeBreadcrumb };
};
